import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { CategoryInterface, TechMapsFilterI } from "../types";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ErrorService from "../../../services/ErrorService";
import { normalizeCategoryData } from "../services/normalizeCategoryData";
import transformProductCategories from "@me-team/host/src/utils/transformProductCategories";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import i18n from "@me-team/host/src/i18n";
import { useGetTechMapsCategoriesQuery } from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetTechMapCategoriesQuery } from "@me-team/host/main/techMapCategories/graphql/techMapCategories.hooks";
import { TechMapCategoryNewProxy } from "@me-team/host/main/graphql/types";
import { CategoryAndSubcategoryId } from "@me-team/host/src/utils/type";
import { getCategoryAndSubcategoryIds } from "@me-team/host/src/utils/utils";
import { Spinner } from "@services/ui-components";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";

interface ProductsFilterWrapperProps {
    setFilters: React.Dispatch<React.SetStateAction<TechMapsFilterI>>;
}

const TechnologicalMapsFilterWrapper: React.FC<ProductsFilterWrapperProps> = ({ setFilters }) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const dropdownRef = useRef(null);
    const [searchParams] = useSearchParams();
    const categoryId: number = Number(searchParams.get("id")) || null;

    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<CategoryInterface[]>(null);
    const [currentIdCatFilter, setCurrentIdCatFilter] = useState({
        categoryId: null,
        subCategoryId: null,
    });
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [currentCatNameFilter, setCurrentCatNameFilter] = useState<string>("");

    const { data: categoryByIdData, loading: categoryByIdDataLoading } = useGetTechMapCategoriesQuery({
        skip: !categoryId,
        variables: {
            id: categoryId,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const categoryData: TechMapCategoryNewProxy = categoryByIdData?.TechMapCategories?.items?.[0];
    const categoryIds: CategoryAndSubcategoryId = getCategoryAndSubcategoryIds(categoryData);
    const initCategoryName: string = categoryData?.name;

    const { data: techMapsCategoriesData, loading: techMapsCategoryLoading } = useGetTechMapsCategoriesQuery({
        variables: {
            page: 1,
            itemsPerPage: 1000,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            const argCategory = categoryIds?.categoryId ? categoryIds?.categoryId : null;
            const argSubcategory = categoryIds?.subcategoryId ? categoryIds?.subcategoryId : null;

            setSelectedCategory(transformProductCategories(normalizedItems, argCategory, argSubcategory));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    useEffect(() => {
        if (!categoryByIdDataLoading && !techMapsCategoryLoading && categoryByIdData) {
            if (categoryIds) {
                const normalizedCategories = normalizeCategoryData(techMapsCategoriesData);
                const transformedCategories: CategoryInterface[] = transformProductCategories(
                    normalizedCategories,
                    categoryIds.categoryId,
                    categoryIds.subcategoryId
                );

                setFilters((prevFilters) => ({
                    ...prevFilters,
                    categoryId: categoryIds?.subcategoryId ? categoryIds.subcategoryId : categoryIds?.categoryId,
                }));

                setSelectedCategory(transformedCategories);
                setCurrentCatNameFilter(initCategoryName);
            }
        }
    }, [categoryByIdDataLoading, techMapsCategoryLoading, categoryByIdData, techMapsCategoriesData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLInputElement;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                target.id !== "technologicalMapsList-categoryId-field"
            ) {
                setIsPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCategoryIdChange = (categoryId: number) => {
        setCurrentIdCatFilter({ categoryId, subCategoryId: null });
    };
    const handleSubCategoryIdChange = (categoryId: number, subCategoryId: number) => {
        setCurrentIdCatFilter((prevState) => ({
            ...prevState,
            categoryId: categoryId,
            subCategoryId: subCategoryId,
        }));
    };

    const removeQueryParam = (searchParams: URLSearchParams) => {
        if (categoryId !== null) {
            searchParams.delete("id");
            navigate(`/warehouse/technological-maps/${currentCompanyId}/branch/${branchId}`, { replace: true });
        }
    };

    const handleApplyFilter = () => {
        setFilters({ search: "", categoryId: null });
        setFilters({
            search: search ? search : "",
            categoryId: currentIdCatFilter?.subCategoryId
                ? currentIdCatFilter?.subCategoryId
                : currentIdCatFilter?.categoryId,
        });
        setIsPickerOpen(false);
        removeQueryParam(searchParams);
    };

    const resetSelectedCategories = (categories: CategoryInterface[]) => {
        return categories?.map((category) => ({
            ...category,
            isPicked: false,
            isSubcategoryPiked: false,
            subcategoryList: category?.subcategoryList?.map((subcategory) => ({
                ...subcategory,
                isPicked: false,
            })),
        }));
    };

    const handleClearFilter = () => {
        setFilters({
            search: null,
            categoryId: null,
        });
        setCurrentCatNameFilter("");
        setSearch("");
        setSelectedCategory(null);
        setCurrentIdCatFilter({ categoryId: null, subCategoryId: null });
        if (selectedCategory) {
            setSelectedCategory(resetSelectedCategories(selectedCategory));
        }

        setIsPickerOpen(false);
        removeQueryParam(searchParams);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    if (categoryByIdDataLoading || techMapsCategoryLoading) return <Spinner />;

    return (
        <>
            <Row className="gx-3">
                <Col lg={8} xxl={9}>
                    <Row className="gx-3 h-100">
                        <Col lg={6}>
                            <div className="position-relative mb-3 mb-lg-0">
                                <input
                                    name="search"
                                    id="technologicalMapsList-search-field"
                                    type="text"
                                    className="form-control px-6 pe-4"
                                    placeholder={t("Search by name")}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value.trimStart())}
                                    onBlur={(e) => setSearch(e.target.value.trim())}
                                    onKeyDown={handleSearchKeyDown}
                                />
                                <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className="position-relative mb-3 mb-lg-0">
                                <input
                                    type="text"
                                    id="technologicalMapsList-categoryId-field"
                                    className="form-control ps-6 pe-4 cursor-pointer"
                                    placeholder={t("Category")}
                                    value={currentCatNameFilter}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsPickerOpen((prev) => !prev);
                                    }}
                                    readOnly
                                />
                                <i
                                    className={`bi bi-chevron-down position-absolute top-50 end-0 text-dark me-2 pointer-event-none transition-transform-icon ${isPickerOpen ? "rotate-180-icon" : ""}`}
                                ></i>
                                <div
                                    ref={dropdownRef}
                                    className="dropdown position-absolute opacity-1 z-2 top-10  start-0 end-0 mt-2 bg-white shadow"
                                >
                                    {techMapsCategoriesData && selectedCategory && (
                                        <CategoryPickerComponent
                                            i18n={i18next}
                                            isOpen={isPickerOpen}
                                            onSearchChange={() => {}}
                                            onChangeCategory={(currentCategory: CategoryPickerCategory) => {
                                                if (currentCategory.isPicked) {
                                                    setCurrentCatNameFilter(currentCategory?.name);
                                                    handleCategoryIdChange(currentCategory?.id);
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentCatNameFilter("");
                                                }
                                            }}
                                            onChangeSubcategory={(currentSubcategory, currentCategory) => {
                                                if (currentSubcategory?.isPicked) {
                                                    setCurrentCatNameFilter(currentSubcategory?.name);
                                                    handleSubCategoryIdChange(
                                                        currentCategory?.id,
                                                        currentSubcategory?.id
                                                    );
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentCatNameFilter("");
                                                }
                                            }}
                                            serviceCategories={selectedCategory}
                                        />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
                <Col lg={4} xxl={3}>
                    <Row className="gx-3 h-100">
                        <Col sm={6} className="mb-3 mb-lg-0">
                            <Button
                                type="button"
                                variant="outline-dark"
                                className="border-grey w-100 h-100 px-1 fw-normal"
                                onClick={handleClearFilter}
                            >
                                {t("clear")}
                            </Button>
                        </Col>
                        <Col sm={6} className="mb-3 mb-lg-0">
                            <Button
                                variant="primary"
                                className="w-100 h-100 px-1 fw-normal"
                                onClick={handleApplyFilter}
                            >
                                {t("apply")}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default TechnologicalMapsFilterWrapper;
